import type { AdminCrewReportImportBatchResultProps } from "../../models/admin-crew-report.models";
import { getAdminCrewReportImportEmailGroups } from "../../utils/admin-crew-report-import.utils";
import AdminCrewReportImportEmailGroup from "./AdminCrewReportImportEmailGroup";
import { twMerge } from "tailwind-merge";

const AdminCrewReportImportBatchResult = ({
  batchNumber,
  batchCount,
  report,
}: AdminCrewReportImportBatchResultProps) => {
  const emailGroups = getAdminCrewReportImportEmailGroups(report.items);
  const hasFailures = report.outcome !== "COMPLETED";
  const isSingleBatch = batchCount === 1;
  const headingId = `admin-crew-report-import-batch-${batchNumber}-heading`;
  const SectionHeading = isSingleBatch ? "h3" : "h4";

  return (
    <section
      aria-label={isSingleBatch ? "Import details" : undefined}
      aria-labelledby={isSingleBatch ? undefined : headingId}
      data-test={`admin-crew-report-import-batch-${batchNumber}`}
      className="admin-crew-report-import-print-batch grid gap-4 border border-[#D8E6E3] bg-[#F8FBFA] p-4 dark:border-gray-700 dark:bg-gray-900"
    >
      {!isSingleBatch && (
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 id={headingId} className="text-lg font-semibold">
              Batch {batchNumber} of {batchCount}
            </h3>
            <p className="mt-1 break-all text-xs text-[#596563] dark:text-gray-300">Cycle {report.cycleId}</p>
          </div>
          <span
            className={twMerge(
              "inline-flex px-2 py-1 text-[11px] font-bold uppercase tracking-wide",
              hasFailures
                ? "bg-[#FDECEC] text-[#BF0002] dark:bg-red-950/40 dark:text-red-200"
                : "bg-[#E7F5F2] text-green dark:bg-teal-950/50 dark:text-teal-200",
            )}
          >
            {report.outcome.replace(/_/g, " ")}
          </span>
        </header>
      )}

      {!isSingleBatch && (
        <dl className="admin-crew-report-import-print-batch-details grid gap-3 text-xs text-[#596563] dark:text-gray-300 sm:grid-cols-2 lg:grid-cols-4">
          <div className="sm:col-span-2">
            <dt className="font-semibold uppercase">Mailbox window</dt>
            <dd className="break-all">
              <time dateTime={report.windowStartedAt}>{report.windowStartedAt}</time> –{" "}
              <time dateTime={report.windowEndedAt}>{report.windowEndedAt}</time>
            </dd>
          </div>
          <div>
            <dt className="font-semibold uppercase">Batch started</dt>
            <dd className="break-all">
              <time dateTime={report.startedAt}>{report.startedAt}</time>
            </dd>
          </div>
          <div>
            <dt className="font-semibold uppercase">Batch completed</dt>
            <dd className="break-all">
              <time dateTime={report.completedAt}>{report.completedAt}</time>
            </dd>
          </div>
          {report.resumedContinuation && (
            <div>
              <dt className="font-semibold uppercase">Resumed continuation</dt>
              <dd>Yes</dd>
            </div>
          )}
          {report.continuationPending && (
            <div>
              <dt className="font-semibold uppercase">Continuation pending</dt>
              <dd>Yes</dd>
            </div>
          )}
          {report.retryableItemsPending && (
            <div>
              <dt className="font-semibold uppercase">Retryable items pending</dt>
              <dd>Yes</dd>
            </div>
          )}
          <div>
            <dt className="font-semibold uppercase">Matching emails</dt>
            <dd>{report.counts.scannedMessages.toLocaleString()}</dd>
          </div>
          <div>
            <dt className="font-semibold uppercase">Excel attachments</dt>
            <dd>{report.counts.candidateAttachments.toLocaleString()}</dd>
          </div>
          <div>
            <dt className="font-semibold uppercase">Imported</dt>
            <dd>{report.counts.imported.toLocaleString()}</dd>
          </div>
          <div>
            <dt className="font-semibold uppercase">Failed</dt>
            <dd>{report.counts.failed.toLocaleString()}</dd>
          </div>
          <div>
            <dt className="font-semibold uppercase">Superseded</dt>
            <dd>{report.counts.superseded.toLocaleString()}</dd>
          </div>
          <div>
            <dt className="font-semibold uppercase">Already processed</dt>
            <dd>{report.counts.alreadyProcessed.toLocaleString()}</dd>
          </div>
        </dl>
      )}

      {report.cycleErrors.length > 0 && (
        <section className="bg-[#FDECEC] px-4 py-3 text-sm text-[#7A2728] dark:bg-red-950/40 dark:text-red-200">
          <SectionHeading className="font-semibold">Cycle errors</SectionHeading>
          <ul className="mt-2 grid list-disc gap-1 pl-5">
            {report.cycleErrors.map((reason, index) => (
              <li key={`${report.cycleId}-cycle-error-${index}`}>{reason}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="admin-crew-report-import-print-email-results grid gap-3">
        <SectionHeading className="font-semibold">Email and attachment results</SectionHeading>
        {emailGroups.length > 0 ? (
          emailGroups.map((items) => (
            <AdminCrewReportImportEmailGroup key={`${report.cycleId}-${items[0]?.messageId}`} items={items} />
          ))
        ) : (
          <p className="bg-white px-4 py-4 text-sm dark:bg-gray-800">
            No matching Crew Report Excel attachments were found.
          </p>
        )}
      </section>

      <section
        className={twMerge(
          "admin-crew-report-import-print-notification px-4 py-3 text-sm",
          report.notification.sent
            ? "bg-[#E7F5F2] text-green dark:bg-teal-950/40 dark:text-teal-200"
            : "bg-[#FDECEC] text-[#7A2728] dark:bg-red-950/40 dark:text-red-200",
        )}
      >
        <SectionHeading className="font-semibold">
          {isSingleBatch ? "Support notification" : "Batch support notification"}
        </SectionHeading>
        <dl className="mt-2">
          <div>
            <dt className="font-semibold">Support notification subject</dt>
            <dd className="break-all">{report.emailSubject}</dd>
          </div>
        </dl>
        <p className="mt-1">
          Attempted: {report.notification.attempted ? "Yes" : "No"}. Sent: {report.notification.sent ? "Yes" : "No"}.
        </p>
        <p className="mt-1">
          {report.notification.sent
            ? `Sent to ${report.mailbox}.`
            : report.notification.attempted
              ? `Could not send this batch summary to ${report.mailbox}. Export the run report for follow-up.`
              : `A support notification was not attempted for ${report.mailbox}.`}
        </p>
        {report.notification.errors.length > 0 && (
          <ul className="mt-2 grid list-disc gap-1 pl-5">
            {report.notification.errors.map((reason, index) => (
              <li key={`${report.cycleId}-notification-error-${index}`}>{reason}</li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
};

export default AdminCrewReportImportBatchResult;
